import { Component, OnDestroy } from '@angular/core';
import { RoomExaminationService } from './room-examination.service';
import {
  KeyValue,
  KeyValueTableComponent,
} from '../../../../reusable-components/key-value-table/key-value-table.component';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PICTURE_MANAGER_TOKEN,
  PictureManagerComponent,
} from '../../../../reusable-components/picture-manager/picture-manager.component';
import { KeyValueTableService } from '../../../../reusable-components/key-value-table/key-value-table.service';
import { DialogService } from '../../../../services/dialog/dialog.service';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { TariffSelectionComponent } from './tariff-selection/tariff-selection.component';
import { combineLatest, Subscription } from 'rxjs';
import { FooterService } from '../../../../reusable-components/footer/footer.service';
import { MatStepperService } from '../../lead-task-services/mat-stepper.service';
import {
  getSortedUsageSpaceCategories,
  getUsageSpaceCategoryWithEmoji,
  UsageSpaceCategory,
} from '../../../../lead.model';
import { LeadTaskService } from '../../lead-task.service';
import { RoomNotVisitedComponent } from './room-not-visited/room-not-visited.component';

@Component({
  selector: 'app-room-examination',
  imports: [
    KeyValueTableComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatIcon,
    TariffSelectionComponent,
    TranslatePipe,
    PictureManagerComponent,
    AsyncPipe,
    RoomNotVisitedComponent,
  ],
  templateUrl: './room-examination.component.html',
  styleUrl: './room-examination.component.scss',
  providers: [
    RoomExaminationService,
    {
      provide: PICTURE_MANAGER_TOKEN,
      useFactory: (roomExaminationService: RoomExaminationService) => {
        return {
          getImages: () => roomExaminationService.getImages(),
          addImage: (image: string) => roomExaminationService.addImage(image),
          removeImageWithDescription: (index: number) =>
            roomExaminationService.removeImageWithDescription(index),
          addDescription: (index: number, description: string) =>
            roomExaminationService.addDescription(index, description),
          getDescription: (index: number) =>
            roomExaminationService.getDescription(index),
        };
      },
      deps: [RoomExaminationService],
    },
  ],
})
export class RoomExaminationComponent implements OnDestroy {
  /**
   * we use an extra variable instead of hardcoded since rerendering of that panel would reset the
   * hardcoded value and this variable is only resetted at component initialization
   */
  detailInformationPanelExpanded = true;

  defaultError = 'components.room-examination.default-error';
  keyValues: KeyValue[];
  matStepperSubscription: Subscription;

  constructor(
    public roomExaminationService: RoomExaminationService,
    private readonly translateService: TranslateService,
    private readonly dialogService: DialogService,
    private readonly snackbarService: SnackbarService,
    private readonly footerService: FooterService,
    private readonly matStepperService: MatStepperService,
    public leadTaskService: LeadTaskService,
  ) {
    this.keyValues = this.initializeKeyValues();

    this.matStepperSubscription = this.getMatStepperSubscription();
    this.roomExaminationService.errorListService.addError(this.defaultError);
  }

  getMatStepperSubscription(): Subscription {
    return combineLatest([
      this.matStepperService.currentStep$,
      this.roomExaminationService.getNgComponentOutletInputObservable(),
      this.roomExaminationService.errorListService.errors$,
    ]).subscribe(([currentIndex, ngComponentOutletInput, errors]) => {
      if (
        currentIndex === 1 &&
        ngComponentOutletInput.component === RoomExaminationComponent
      ) {
        this.footerService.setRightButton({
          label: 'components.room-examination.save',
          click: () => this.roomExaminationService.saveRoomExamination(),
          disabled: errors.length > 1,
        });
        this.footerService.setLeftButton({
          label: 'components.room-examination.discard-button.label',
          click: () => {
            this.dialogService.openYesNoDialog(
              'components.room-examination.discard-button.question',
              'components.room-examination.discard-button.yes',
              'components.room-examination.discard-button.no',
              () => this.roomExaminationService.discardRoomExamination(),
            );
          },
        });
      }
    });
  }

  initializeKeyValues(): KeyValue[] {
    const keyValueTableService = KeyValueTableService.create(
      this.translateService,
      this.dialogService,
      this.snackbarService,
    );
    const isHotelRoom =
      this.roomExaminationService.getCategory() === 'HOTEL_GAESTEZIMMER';

    const keyValues: KeyValue[] = [
      keyValueTableService.createUpdateKeyValuePair({
        key: 'components.usage-space.information-table.name',
        updateValueFn: (value: string) =>
          this.roomExaminationService.setName(value),
        getValueFn: () => this.roomExaminationService.getName(),
        acceptInputFn: (value: string) =>
          this.leadTaskService.validateUsageSpaceName(value),
        errorMessage: 'components.room-examination.name-error',
      }),
      this.getChangeRoomCategoryKeyValuePair(),
    ];

    if (isHotelRoom) {
      // Hotel-/Gästezimmer: show room count (read-only from SAP), hide size
      keyValues.push(
        keyValueTableService.createKeyValuePair(
          'components.usage-space.information-table.numberRooms',
          this.roomExaminationService.getNumberRooms().toString(),
        ),
      );
    } else {
      // All other categories: show size, hide room count
      keyValues.push(
        keyValueTableService.createUpdateKeyValuePair({
          key: 'components.usage-space.information-table.size',
          updateValueFn: (value: string) =>
            this.roomExaminationService.setSize(value),
          getValueFn: () => this.roomExaminationService.getSize().toString(),
          acceptInputFn: (value: string) =>
            this.leadTaskService.validateUsageSpaceSize(value),
          errorMessage: 'components.room-examination.size-error',
          type: 'number',
        }),
      );
    }

    return keyValues;
  }

  getChangeRoomCategoryKeyValuePair(): KeyValue {
    const keyValue: KeyValue = {
      key: 'components.usage-space.information-table.category',
      value: getUsageSpaceCategoryWithEmoji(
        this.roomExaminationService.getCategory(),
        this.translateService,
      ),
      tryUpdate: () => {
        const onSelect = (option: string) => {
          this.roomExaminationService.setCategory(option as UsageSpaceCategory);
          keyValue.value = getUsageSpaceCategoryWithEmoji(
            option as UsageSpaceCategory,
            this.translateService,
          );
          this.snackbarService.show('components.input-dialog.snackbar');
          // Re-initialize table so size/numberRooms visibility updates
          this.keyValues = this.initializeKeyValues();
        };
        this.dialogService.openSelectDialog(
          getSortedUsageSpaceCategories(this.translateService)
            .filter(
              (category) =>
                category !== 'HOTEL_GAESTEZIMMER' ||
                this.leadTaskService.getLeadCategory() ===
                'GASTHOF_GASTHAUS_MIT_UEBERNACHTUNG',
            )
            .map((category) => {
              return {
                option: category,
                label: getUsageSpaceCategoryWithEmoji(category, this.translateService),
              };
            }),
          'components.usage-space.information-table.category',
          'components.room-examination.select-category-question',
          onSelect,
          undefined,
          this.roomExaminationService.getCategory(),
        );
      },
      translateValue: false,
    };
    return keyValue;
  }

  ngOnDestroy(): void {
    this.matStepperSubscription.unsubscribe();
    this.roomExaminationService.errorListService.removeError(this.defaultError);
  }
}
